import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";
import MainContainer from "./layout/MainContainer";
import Pages from "./pages";
import { getFromStorage } from "./helpers";
import { STORAGE_KEYS } from "./constants";

// Component to protect auth routes (login, forgot password, etc.)
const AuthRoute = ({ children }: { children: JSX.Element }) => {
  const token = getFromStorage(STORAGE_KEYS.token);
  const location = useLocation();

  // If user is already authenticated, redirect to dashboard
  if (token) {
    // Preserve the intended destination if available, otherwise go to dashboard
    const from = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={from} replace state={{ from: location }} />;
  }

  return children;
};

// Component to protect authenticated routes
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const token = getFromStorage(STORAGE_KEYS.token);
  const location = useLocation();

  // If user is not authenticated, redirect to login
  if (!token) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return children;
};

const Routing = () => {
  // Memoize the MainContainer to prevent unnecessary re-renders
  const MainContainerWrapper = useMemo(
    () => (
      <ProtectedRoute>
        <MainContainer />
      </ProtectedRoute>
    ),
    []
  );

  return (
    <Routes>
      {/* Public auth routes */}
      <Route
        path="/"
        element={
          <AuthRoute>
            <Pages.Login />
          </AuthRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <AuthRoute>
            <Pages.ForgotPassword />
          </AuthRoute>
        }
      />
      <Route
        path="/otp"
        element={
          <AuthRoute>
            <Pages.OtpVerification />
          </AuthRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <AuthRoute>
            <Pages.ResetPassword />
          </AuthRoute>
        }
      />

      {/* Protected routes */}
      <Route element={MainContainerWrapper}>
        <Route path="/dashboard" element={<Pages.Dashboard />} />
        <Route path="/analytics" element={<Pages.Analytics />} />
        <Route path="/manage-users" element={<Pages.ManageUser />} />
        <Route
          path="/manage-users/detail"
          element={<Pages.ManageUserDetail />}
        />
        <Route
          path="/manage-users/detail/:id"
          element={<Pages.ManageUserDetail />}
        />
        <Route path="/manage-users/edit/:id" element={<Pages.Edituser />} />
        <Route path="/manage-users/add" element={<Pages.Edituser />} />
        <Route path="/manage-subadmin" element={<Pages.ManageSubadmin />} />
        <Route path="/manage-subadmin/add" element={<Pages.AddSubadmin />} />
        <Route
          path="/manage-subadmin/edit/:id"
          element={<Pages.AddSubadmin />}
        />
        <Route
          path="/manage-subadmin/detail/:id"
          element={<Pages.SubadminDetail />}
        />
        <Route
          path="/manage-subadmin/roles"
          element={<Pages.ManageSubadminRoles />}
        />
        <Route path="/manage-subadmin/roles/add" element={<Pages.AddRole />} />
        {/* <Route path="/manage-subadmin/roles/edit/:id" element={<Pages.AddRole />} /> */}
        <Route
          path="/manage-subadmin/roles/detail/:id"
          element={<Pages.RoleDetail />}
        />

        <Route
          path="/manage-notifications"
          element={<Pages.ManageNotifications />}
        />
        <Route
          path="/manage-notifications/add"
          element={<Pages.AddNotification />}
        />
        <Route
          path="/manage-notifications/detail/:id"
          element={<Pages.NotificationDetail />}
        />
        <Route
          path="/recieved-notifications"
          element={<Pages.RecievedNotifications />}
        />
        <Route
          path="/manage-system-settings"
          element={<Pages.ManageSystemSettings />}
        />
        <Route
          path="/manage-system-settings/add-email-template"
          element={<Pages.AddEmailTemplate />}
        />
        <Route path="/manage-cms" element={<Pages.ManageCms />} />
        <Route path="/manage-profile" element={<Pages.ManageProfile />} />
        <Route path="/manage-profile/edit" element={<Pages.EditProfile />} />
        <Route
          path="/manage-profile/change-password"
          element={<Pages.ChangePassword />}
        />

        <Route path="/manage-categories" element={<Pages.ManageCategories />} />
        <Route path="/manage-categories/add" element={<Pages.AddCategory />} />
        <Route
          path="/manage-categories/edit/:id"
          element={<Pages.AddCategory />}
        />
        <Route
          path="/manage-categories/detail/:id"
          element={<Pages.ViewPromotion />}
        />
        <Route path="/manage-products" element={<Pages.ManageProducts />} />
        <Route path="/manage-products/add" element={<Pages.AddProduct />} />
        <Route
          path="/manage-products/edit/:id"
          element={<Pages.AddProduct />}
        />
        <Route
          path="/manage-products/detail/:id"
          element={<Pages.ProductDetail />}
        />
        <Route path="/manage-qr-code" element={<Pages.ManageQrCode />} />
        <Route
          path="/manage-boost-plans"
          element={<Pages.ManageBoostPlans />}
        />
        <Route
          path="/manage-boost-plans/add"
          element={<Pages.AddBoostPlans />}
        />
        <Route
          path="/manage-boost-plans/edit/:id"
          element={<Pages.AddBoostPlans />}
        />
        <Route
          path="/manage-boost-plans/detail/:id"
          element={<Pages.BoostPlanDetail />}
        />
        <Route path="/manage-payments" element={<Pages.ManagePayments />} />
        <Route path="/manage-payments/view" element={<Pages.ViewPayments />} />
        <Route
          path="/manage-activity-logs"
          element={<Pages.ManageActivityLogs />}
        />
        <Route path="/customer-supports" element={<Pages.Support />} />
        <Route path="/customer-supports/view" element={<Pages.SupportView />} />
        <Route path="/customer-supports/chat" element={<Pages.SupportChat />} />
        <Route path="/manage-reviews" element={<Pages.ManageReviews />} />
        <Route
          path="/manage-reviews/view/:id"
          element={<Pages.ReviewsDetail />}
        />
      </Route>

      {/* Catch-all route for 404 */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default Routing;
